
import SearchIcon from '@mui/icons-material/Search'
import Button from '@mui/material/Button'
import Input from '@mui/material/Input'
import AdminPage from 'admin/components/AdminPage'
import { IQuote } from 'admin/interfaces'
import { getQuotes, getQuotesRange, getTotalQuotesCount } from 'admin/services/quotes/actions'
import ScrollListener from 'components/ScrollListener'
import useAsyncAction from 'hooks/useAsyncAction'
import * as React from 'react'
import { useDispatch } from 'react-redux'
import { NavLink } from 'react-router'
import { of as observableOf, Subject } from 'rxjs'
import { catchError, mergeMap } from 'rxjs/operators'
import { message } from 'services/snackbar'
import { StoreThunkDispatch } from 'store/state'
import { CreateStatusIdeaPanel } from './CreateStatusIdeaPanel'
import styles from './Quotes.pcss'
import { QuotesTable } from './QuotesTable'

const SCROLL_EMIT_TRESHOLD = 450

export function QuotesRoute() {
  const dispatch = useDispatch<StoreThunkDispatch>()
  const [quotes, setQuotes] = React.useState<IQuote[]>([])
  const [loading, setLoading] = React.useState(true)
  const [query, setQuery] = React.useState('')
  const [tagQuery, setTagQuery] = React.useState('')
  const [hasNextPage, setHasNextPage] = React.useState(true)
  const [getCount, count, _loading] = useAsyncAction(() => dispatch(getTotalQuotesCount()))

  const isTagSearch = React.useRef(false)
  const isRangeSearch = React.useRef(false)
  const pageRef = React.useRef(0)
  const startIDRef = React.useRef<HTMLInputElement>(null)
  const endIDRef = React.useRef<HTMLInputElement>(null)
  const fetch$ = React.useRef<Subject<{ page: number, query: string, isTag: boolean }>>(undefined)
  const fetchRange$ = React.useRef<Subject<{ from: number, to: number }>>(undefined)

  React.useEffect(() => {
    fetch$.current = new Subject()
    fetch$.current.pipe(
      mergeMap(({ page, query, isTag }) => {
        isTagSearch.current = isTag
        isRangeSearch.current = false
        if (startIDRef.current && endIDRef.current) {
          startIDRef.current.value = ''
          endIDRef.current.value = ''
        }
        setLoading(true)
        return dispatch(getQuotes(page, isTag ? '' : query, isTag ? query : undefined)).pipe(catchError(() => {
          dispatch(message('Could not fetch data! Please refresh.'))
          return observableOf([])
        }))
      }))
      .subscribe((response: any) => {
        if (response.length > 0) {
          setQuotes(current => [...current, ...response])
        } else {
          setHasNextPage(false)
        }
        setLoading(false)
      })

    fetch$.current.next({ page: 0, query: '', isTag: false })
    getCount()
  }, [dispatch, getCount])

  React.useEffect(() => {
    fetchRange$.current = new Subject()
    fetchRange$.current.pipe(
      mergeMap(({ from, to }) => {
        isTagSearch.current = false
        isRangeSearch.current = true
        setLoading(true)
        return dispatch(getQuotesRange(from, to)).pipe(catchError(() => {
          dispatch(message('Could not fetch data! Please refresh.'))
          return observableOf([])
        }))
      }))
      .subscribe((response: any) => {
        if (response.length > 0) {
          setQuotes(response)
        } else {
          setHasNextPage(false)
        }
        setLoading(false)
      })
  }, [dispatch])

  const onSearchKeyDown = (isTag?: boolean) => (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      pageRef.current = 0
      setQuotes([])
      fetch$.current?.next({
        page: pageRef.current,
        query: (e.target as HTMLInputElement).value.trim(),
        isTag: Boolean(isTag)
      })

      if (isTag) {
        setQuery('')
      } else {
        setTagQuery('')
      }
    }
  }

  const onSearchChange = (isTag?: boolean) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setHasNextPage(true)
    if (isTag) {
      setTagQuery(value)
    } else {
      setQuery(value)
    }
  }

  const loadNextPage = () => {
    if (!loading && hasNextPage && !isRangeSearch.current) {
      pageRef.current += 1
      fetch$.current?.next({
        page: pageRef.current,
        query: query.trim(),
        isTag: isTagSearch.current
      })
    }
  }

  const resetSearch = () => {
    pageRef.current = 0
    setQuotes([])
    setTagQuery('')
    setQuery('')
    getCount()
    fetch$.current?.next({
      page: pageRef.current,
      query: '',
      isTag: false
    })
  }

  const getQuotesForRange = () => {
    try {
      const startId = parseInt(startIDRef.current?.value + '', 10)
      const endId = parseInt(endIDRef.current?.value + '', 10)

      if (startId > endId) {
        throw new Error('Start ID must be less or equal to End ID')
      }
      pageRef.current = 0
      setQuotes([])
      setTagQuery('')
      setQuery('')

      fetchRange$.current?.next({ from: startId, to: endId })
    } catch (e: any) {
      dispatch(message('Enter valid values for start and end ID! ' + e?.message))
    }
  }

  const scrollElement = document.querySelector('[data-test="admin-content"]') as HTMLElement

  return (
    <AdminPage title="Quotes">
      <NavLink to="/admin/product/quotes/tags">Tags</NavLink> | <NavLink to="/admin/product/quotes/keywords">Keywords</NavLink>
      <ScrollListener
        emitTreshold={SCROLL_EMIT_TRESHOLD}
        scrollElement={scrollElement}
        onScroll={loadNextPage}
      >
        <div>
          <CreateStatusIdeaPanel onCreated={resetSearch} />
          <div className={styles['search-box']}>
            <Button variant="contained" size="small" color="primary" onClick={resetSearch}>
              see all{count ? ` (${count})` : ''}
            </Button>
            <Input
              startAdornment={<SearchIcon />}
              placeholder="Search quotes"
              value={query}
              classes={{ root: styles['search-input'] }}
              onChange={onSearchChange()}
              onKeyDown={onSearchKeyDown()}
            />
            <Input
              startAdornment={<SearchIcon />}
              placeholder="Search by tag"
              value={tagQuery}
              classes={{ root: styles['search-input'] }}
              onChange={onSearchChange(true)}
              onKeyDown={onSearchKeyDown(true)}
            />
          </div>
          <div className={`${styles['search-box']} ${styles['search-range-box']}`}>
            Show quotes between ID
            <input type="number" ref={startIDRef} />
            And
            <input type="number" ref={endIDRef} />
            <Button variant="contained" color="primary" size="small" onClick={getQuotesForRange}>Show quotes</Button>
          </div>
          <QuotesTable quotes={quotes} />
          {loading && <h3 className={styles.loader}>Loading...</h3>}
          {!loading && !hasNextPage && quotes.length > 0 && <h3 className={styles.loader}>That's all!</h3>}
          {!loading && !hasNextPage && quotes.length === 0 && <h3 className={styles.loader}>No results found.</h3>}
        </div>
      </ScrollListener>
    </AdminPage>
  )
}

export default QuotesRoute
